import { downloadExternalUrl } from 'approot/shared/download-doc/download-doc';
import type {
  GetStimulusResourceQuery,
  GetStimulusResourceQueryVariables,
} from '../shared/stimulus/stimulus.graphql.generated';
import { GET_STIMULUS_RESOURCE } from 'approot/shared/stimulus/stimulus.graphql';
import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import type { Pluck } from 'lib/gql/pluck';

export const useGuidedReadThroughDownload = (
  refetch?: () => Promise<{ data: GetStimulusResourceQuery }>,
  isSignedIn?: boolean
) => {
  const [getStimulusResource] = useLoggingLazyQuery<
    GetStimulusResourceQuery,
    GetStimulusResourceQueryVariables
  >(
    GET_STIMULUS_RESOURCE,
    {
      fetchPolicy: 'network-only',
    },
    isSignedIn ?? false
  );

  const downloadGuidedReadThrough = async (
    guidedReadThrough: Pluck<
      Extract<
        Pluck<GetStimulusResourceQuery, 'getStimulusResource'>,
        { __typename: 'StimulusResourceEBookInteractive' }
      >,
      'guidedReadThroughs'
    >,
    isSignedIn: boolean,
    stimulusResourceId: number
  ): Promise<boolean> => {
    // If the GRT already has a link, download
    if (guidedReadThrough.link && guidedReadThrough.name) {
      await downloadExternalUrl(
        guidedReadThrough.link,
        guidedReadThrough.name,
        'pdf'
      );
      return true;
    }

    // If user isn't signed in yet and link is null, return false
    if (!isSignedIn) return false;

    // Fetch fresh data (use parent refetch if available, else fallback to lazy query)
    const fresh = refetch
      ? (await refetch()).data
      : (
          await getStimulusResource({
            variables: { input: { id: stimulusResourceId } },
          })
        ).data;

    if (!fresh) return false;

    const stimulusResource = fresh.getStimulusResource;
    if (stimulusResource?.__typename !== 'StimulusResourceEBookInteractive') {
      return false;
    }
    // find and update the guidedReadThroughs with the link
    const updatedGuidedReadThrough = stimulusResource.guidedReadThroughs?.find(
      grt => grt.id === guidedReadThrough.id
    );

    if (!updatedGuidedReadThrough?.link || !updatedGuidedReadThrough.name)
      return false;

    // Download using fresh link
    await downloadExternalUrl(
      updatedGuidedReadThrough.link,
      updatedGuidedReadThrough.name,
      'pdf'
    );
    return true;
  };

  return { downloadGuidedReadThrough };
};
